import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

export interface TableStylesOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableStyles: {
      toggleTransparentTable: () => ReturnType;
      setTransparentTable: () => ReturnType;
      unsetTransparentTable: () => ReturnType;
      toggleWarningTable: () => ReturnType;
      setWarningTable: () => ReturnType;
      unsetWarningTable: () => ReturnType;
      clearTableStyles: () => ReturnType;
      setTableWidth: (width: string) => ReturnType;
      clearTableWidth: () => ReturnType;
    };
  }
}

export const TableStyles = Extension.create<TableStylesOptions>({
  name: "tableStyles",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addCommands() {
    return {
      toggleTransparentTable:
        () =>
        ({ commands, editor }) => {
          // Check if we're currently in a table
          if (!editor.isActive("table")) {
            return false;
          }

          const tableAttributes = editor.getAttributes("table");
          const isTransparent =
            tableAttributes["data-table-style"] === "transparent";

          if (isTransparent) {
            return commands.clearTableStyles();
          } else {
            return commands.setTransparentTable();
          }
        },

      setTransparentTable:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.updateAttributes("table", {
            "data-table-style": "transparent",
          });
        },

      unsetTransparentTable:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.clearTableStyles();
        },

      toggleWarningTable:
        () =>
        ({ commands, editor }) => {
          // Check if we're currently in a table
          if (!editor.isActive("table")) {
            return false;
          }

          const tableAttributes = editor.getAttributes("table");
          const isWarning = tableAttributes["data-table-style"] === "warning";

          if (isWarning) {
            return commands.clearTableStyles();
          } else {
            return commands.setWarningTable();
          }
        },

      setWarningTable:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.updateAttributes("table", {
            "data-table-style": "warning",
          });
        },

      unsetWarningTable:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.clearTableStyles();
        },

      clearTableStyles:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.updateAttributes("table", {
            "data-table-style": null,
          });
        },

      setTableWidth:
        (width: string) =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.updateAttributes("table", {
            "data-table-width": width,
          });
        },

      clearTableWidth:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive("table")) {
            return false;
          }

          return commands.updateAttributes("table", {
            "data-table-width": null,
          });
        },
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: ["table"],
        attributes: {
          "data-table-style": {
            default: null,
            parseHTML: (element) => element.getAttribute("data-table-style"),
            renderHTML: (attributes) => {
              const style = attributes["data-table-style"];
              const width = attributes["data-table-width"];

              let styleString = "";
              let className = "";
              const dataAttrs: Record<string, string> = {};

              // Handle table style
              if (style) {
                dataAttrs["data-table-style"] = style;

                if (style === "transparent") {
                  className = "transparent-table";
                  styleString +=
                    "border: none !important; border-collapse: collapse; background: transparent !important;";
                } else if (style === "warning") {
                  className = "warning-table";
                  styleString +=
                    "border: 2px solid rgba(245,135,19,1) !important; border-collapse: collapse;";
                }
              }

              // Handle table width - always apply width if data-table-width exists
              if (width) {
                dataAttrs["data-table-width"] = width;
                styleString += ` width: ${width} !important; min-width: ${width} !important;`;
              }

              // Always return data attributes and styles if they exist
              if (!style && !width) {
                return {};
              }

              const result: Record<string, string> = { ...dataAttrs };
              if (className) result["class"] = className;
              if (styleString) result["style"] = styleString;

              return result;
            },
          },
          "data-table-width": {
            default: null,
            parseHTML: (element) => element.getAttribute("data-table-width"),
            renderHTML: () => {
              // This is handled in the data-table-style renderHTML function above
              return {};
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      // Plugin to ensure table widths from data-table-width are always applied to CSS
      new Plugin({
        key: new PluginKey("tableWidthSync"),
        view(editorView: EditorView) {
          const syncTableWidths = () => {
            const dom = editorView.dom;
            const tables = dom.querySelectorAll("table[data-table-width]");

            tables.forEach((table: Element) => {
              const tableElement = table as HTMLTableElement;
              const width = tableElement.getAttribute("data-table-width");

              if (
                width &&
                (!tableElement.style.width ||
                  !tableElement.style.width.includes(width))
              ) {
                tableElement.style.width = width;
                tableElement.style.setProperty("width", width, "important");
                tableElement.style.setProperty("min-width", width, "important");
              }
            });
          };

          // Initial sync
          setTimeout(syncTableWidths, 0);

          return {
            update: syncTableWidths,
          };
        },
      }),
    ];
  },
});
